import { Add } from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Grid,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import BatchSaleEntry from '../../components/checkout/BatchSaleEntry';
import SalesChannelSelect from '../../components/checkout/SalesChannelSelect';
import DidNotLoad from '../../components/DidNotLoad';
import PhoneInput from '../../components/PhoneInput';
import {
    buildBatchCheckoutPayload,
    createEmptyBatchSale,
    filterBatchSaleErrors,
    syncBatchSalePaymentDates,
} from '../../helpers/batchCheckout';
import { createPaymentRow, validatePaymentRows } from '../../helpers/checkoutPayments';
import { formatCurrencyInCents, formatPhone } from '../../helpers/formatters';
import { applyName } from '../../helpers/captureMode';
import { hasPremiumAccess, readStoredPlanName } from '../../helpers/subscription';
import { channelForcesPaid } from '../../helpers/orderStages';

function createInitialBatchFormState() {
    const saleOccurredAt = new Date();
    return {
        saleOccurredAt,
        sales: [createEmptyBatchSale(saleOccurredAt)],
    };
}

function RegisterBatchCheckoutPage() {
    const navigate = useNavigate();
    const hasPremium = hasPremiumAccess(readStoredPlanName());
    const initialFormState = useMemo(() => createInitialBatchFormState(), []);

    const [salesChannel, setSalesChannel] = useState('');
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [saleOccurredAt, setSaleOccurredAt] = useState(initialFormState.saleOccurredAt);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [sales, setSales] = useState(initialFormState.sales);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });
    const [isLoadingCustomerSearch, setIsLoadingCustomerSearch] = useState(false);
    const [customerResults, setCustomerResults] = useState([]);
    const [anchorElCustomer, setAnchorElCustomer] = useState(null);
    const customerSearchTimerRef = useRef(null);

    const batchTotal = useMemo(
        () => sales.reduce((sum, sale) => sum + (Number(sale.amount) || 0), 0),
        [sales],
    );

    const updateSale = (key, nextSale) => {
        setSales((prev) => prev.map((sale) => (sale.key === key ? nextSale : sale)));
    };

    const handleSaleOccurredAtChange = (nextSaleOccurredAt) => {
        setSales((prev) => syncBatchSalePaymentDates(prev, saleOccurredAt, nextSaleOccurredAt));
        setSaleOccurredAt(nextSaleOccurredAt);
    };

    const addSale = () => {
        setSales((prev) => [...prev, createEmptyBatchSale(saleOccurredAt)]);
    };

    const removeSale = (key) => {
        setSales((prev) => prev.filter((sale) => sale.key !== key));
    };

    useEffect(() => {
        if (!channelForcesPaid(selectedChannel?.kind)) {
            return;
        }
        setSales((prev) => prev.map((sale) => ({
            ...sale,
            customerWillPayLater: false,
            payments: (sale.payments && sale.payments.length > 0)
                ? sale.payments
                : [createPaymentRow({ amountCents: sale.amount || 0, paymentDate: saleOccurredAt || new Date() })],
        })));
    }, [selectedChannel]);

    const customerSearch = (name) => {
        if (name.length >= 3) {
            if (customerSearchTimerRef.current) {
                clearTimeout(customerSearchTimerRef.current);
            }

            customerSearchTimerRef.current = setTimeout(() => {
                setIsLoadingCustomerSearch(true);
                api.get(`customers?q=${name}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.customers)
                            ? d.customers
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        setCustomerResults(rows);
                    })
                    .catch(() => setCustomerResults([]))
                    .finally(() => setIsLoadingCustomerSearch(false));
            }, 1000);
        } else {
            setCustomerResults([]);
        }
    };

    const handleCloseCustomerMenu = () => {
        setAnchorElCustomer(null);
        setCustomerResults([]);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!hasPremium || isSubmitting) {
            return;
        }

        const invalid = {};
        if (!customerName.trim() && !customerId) {
            invalid.customer_name = 'Informe o nome do(a) vendedor(a) ou do cliente.';
        }
        sales.forEach((sale, index) => {
            if (sale.products.length === 0) {
                invalid[`sales.${index}.products`] = 'Inclua ao menos um produto nesta venda.';
            }
            if (!channelForcesPaid(selectedChannel?.kind) && !sale.customerWillPayLater) {
                const paymentError = validatePaymentRows(sale.payments, sale.amount || 0);
                if (paymentError) {
                    invalid[`sales.${index}.payment_methods`] = paymentError;
                }
            }
        });

        if (Object.keys(invalid).length > 0) {
            setResultErrors({ ...invalid, has_errors: true });
            return;
        }

        setIsSubmitting(true);
        setResultErrors({ has_errors: false });

        const payload = buildBatchCheckoutPayload({
            salesChannel,
            saleOccurredAt,
            customerId,
            customerName,
            customerPhone,
            customerEmail,
            sales,
            forcePaid: channelForcesPaid(selectedChannel?.kind),
        });

        api.post('checkouts/batch', payload)
            .then((response) => {
                const data = response.data;
                if (!data?.success) {
                    setResultErrors({
                        ...(data.errors || {}),
                        has_errors: true,
                    });
                    return;
                }

                Swal.fire({
                    icon: 'success',
                    title: 'Vendas registradas',
                    text: `${data.count} venda(s) foram concluídas com sucesso.`,
                    confirmButtonText: 'Ver pedidos',
                }).then(() => navigate('/admin/orders'));
            })
            .catch((error) => {
                const data = error?.response?.data;
                if (data?.errors) {
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                } else if (data?.error) {
                    setResultErrors({
                        batch: data.error,
                        has_errors: true,
                    });
                } else {
                    setDidNotLoad(true);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (!hasPremium) {
        return (
            <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Este recurso não está disponível na sua conta.
                </Alert>
                <Button variant="contained" onClick={() => navigate('/admin/orders/new')}>
                    Voltar ao registro individual
                </Button>
            </Box>
        );
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/orders')} to="/admin/orders">
                    Pedidos
                </Link>
                <Typography color="text.primary">Pedidos em lote</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4">Pedidos em lote</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Os dados globais serão aplicados a cada venda. Cada bloco abaixo vira um pedido separado,
                com seu próprio total e forma de pagamento.
            </Typography>

            <form onSubmit={handleSubmit}>
                <Typography variant="h5" gutterBottom>Dados globais</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <SalesChannelSelect
                            label="Canal de vendas"
                            value={salesChannel}
                            onChange={(e) => setSalesChannel(e.target.value)}
                            onChannelChange={setSelectedChannel}
                        />
                        {resultErrors.sales_channel && (
                            <Typography variant="caption" color="error" display="block">
                                {resultErrors.sales_channel}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                            <DateTimePicker
                                label="Data e hora da venda"
                                value={saleOccurredAt}
                                onChange={handleSaleOccurredAtChange}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            id="customer-name"
                            label={isLoadingCustomerSearch ? 'Buscando vendedor(a) / cliente...' : 'Vendedor(a) / cliente (obrigatório)'}
                            fullWidth
                            value={customerName}
                            onChange={(e) => {
                                setCustomerName(e.target.value);
                                customerSearch(e.target.value);
                            }}
                            onFocus={(e) => setAnchorElCustomer(e.currentTarget)}
                            helperText="Use o nome do(a) vendedor(a) que efetivou as vendas, se for o caso."
                        />
                        <Menu
                            anchorEl={anchorElCustomer}
                            open={Boolean(anchorElCustomer) && customerResults.length > 0}
                            onClose={handleCloseCustomerMenu}
                            disableAutoFocusItem
                        >
                            {customerResults.map((customer) => (
                                <MenuItem
                                    key={customer.uuid || customer.id}
                                    onClick={() => {
                                        setCustomerName(customer.name);
                                        setCustomerEmail(customer.email || '');
                                        setCustomerPhone(formatPhone(customer.phone));
                                        setCustomerId(customer.uuid || customer.id || '');
                                        handleCloseCustomerMenu();
                                    }}
                                >
                                    {applyName(customer.name)}
                                </MenuItem>
                            ))}
                        </Menu>
                        {resultErrors.customer_name && (
                            <Typography variant="caption" color="error" display="block">
                                {resultErrors.customer_name}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <PhoneInput
                            label="Telefone"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="E-mail (opcional)"
                            fullWidth
                            type="email"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Typography variant="h5" sx={{ mb: 2 }}>
                    Vendas do lote
                </Typography>

                {sales.map((sale, index) => (
                    <BatchSaleEntry
                        key={sale.key}
                        index={index}
                        sale={sale}
                        onChange={(nextSale) => updateSale(sale.key, nextSale)}
                        onRemove={() => removeSale(sale.key)}
                        canRemove={sales.length > 1}
                        fieldErrors={filterBatchSaleErrors(resultErrors, index)}
                        defaultPaymentDate={saleOccurredAt}
                        channelKind={selectedChannel?.kind}
                    />
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Button variant="outlined" startIcon={<Add />} onClick={addSale}>
                        Adicionar venda
                    </Button>
                </Box>

                <Typography variant="h5" align="right" sx={{ mb: 3 }}>
                    Total do lote:
                    {' '}
                    {formatCurrencyInCents(batchTotal)}
                    {' '}
                    (
                    {sales.length}
                    {' '}
                    venda(s))
                </Typography>

                {resultErrors.batch && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {resultErrors.batch}
                    </Alert>
                )}

                {resultErrors.has_errors && !resultErrors.batch && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Corrija os erros indicados acima antes de salvar.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button variant="outlined" onClick={() => navigate('/admin/orders/new')}>
                        Registro individual
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmitting ? 'Registrando...' : `Concluir ${sales.length} venda(s)`}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default RegisterBatchCheckoutPage;
